import { useEffect, useState } from "react";
import { Languages, X } from "lucide-react";

/* Translate-this-page dropdown (Google Website Translator, free/client-side).
   Visible on every page via LocationBar. Translates all visible text incl.
   discussion posts. */

const LANGS = [
  ["", "English (original)"],
  // Indian languages first
  ["hi", "हिन्दी (Hindi)"], ["ta", "தமிழ் (Tamil)"], ["te", "తెలుగు (Telugu)"],
  ["ml", "മലയാളം (Malayalam)"], ["kn", "ಕನ್ನಡ (Kannada)"], ["bn", "বাংলা (Bengali)"],
  ["mr", "मराठी (Marathi)"], ["gu", "ગુજરાતી (Gujarati)"], ["pa", "ਪੰਜਾਬੀ (Punjabi)"],
  ["ur", "اردو (Urdu)"], ["or", "ଓଡ଼ିଆ (Odia)"], ["as", "অসমীয়া (Assamese)"],
  ["ne", "नेपाली (Nepali)"], ["si", "සිංහල (Sinhala)"], ["sd", "سنڌي (Sindhi)"],
  // World languages
  ["es", "Español"], ["fr", "Français"], ["de", "Deutsch"], ["it", "Italiano"],
  ["pt", "Português"], ["nl", "Nederlands"], ["pl", "Polski"], ["sv", "Svenska"],
  ["no", "Norsk"], ["da", "Dansk"], ["fi", "Suomi"], ["el", "Ελληνικά"],
  ["cs", "Čeština"], ["sk", "Slovenčina"], ["hu", "Magyar"], ["ro", "Română"],
  ["bg", "Български"], ["uk", "Українська"], ["ru", "Русский"], ["tr", "Türkçe"],
  ["ar", "العربية"], ["fa", "فارسی"], ["he", "עברית"], ["sw", "Kiswahili"],
  ["zh-CN", "中文 (简体)"], ["zh-TW", "中文 (繁體)"], ["ja", "日本語"], ["ko", "한국어"],
  ["th", "ไทย"], ["vi", "Tiếng Việt"], ["id", "Bahasa Indonesia"], ["ms", "Bahasa Melayu"],
  ["fil", "Filipino"], ["km", "ខ្មែរ"], ["my", "မြန်မာ"],
];

function currentLang() {
  const m = document.cookie.match(/(?:^|;\s*)googtrans=\/en\/([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "";
}

export default function TranslatePage() {
  const [lang, setLang] = useState(currentLang);

  useEffect(() => {
    if (document.getElementById("gt-script")) return;
    window.googleTranslateElementInit = () => {
      /* global google */
      new window.google.translate.TranslateElement(
        { pageLanguage: "en", autoDisplay: false }, "google_translate_element");
    };
    const s = document.createElement("script");
    s.id = "gt-script";
    s.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    document.body.appendChild(s);
    const style = document.createElement("style");
    style.textContent = `
      #google_translate_element{display:none}
      .goog-te-banner-frame,.goog-te-balloon-frame,#goog-gt-tt{display:none!important}
      body{top:0!important}
      .goog-text-highlight{background:none!important;box-shadow:none!important}
      .skiptranslate iframe{display:none!important}`;
    document.head.appendChild(style);
  }, []);

  const change = (code) => {
    setLang(code);
    const host = window.location.hostname;
    const setCookie = (v) => {
      document.cookie = `googtrans=${v};path=/`;
      document.cookie = `googtrans=${v};path=/;domain=${host}`;
      document.cookie = `googtrans=${v};path=/;domain=.${host}`;
    };
    if (!code) {
      // reset to original English — expire every googtrans cookie variant (host, .host, parent domains)
      const expire = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
      document.cookie = `googtrans=;path=/;${expire}`;
      const parts = host.split(".");
      for (let i = 0; i < parts.length - 1; i++) {
        const d = parts.slice(i).join(".");
        document.cookie = `googtrans=;path=/;domain=${d};${expire}`;
        document.cookie = `googtrans=;path=/;domain=.${d};${expire}`;
      }
      window.location.reload();
      return;
    }
    setCookie(`/en/${code}`);
    const combo = document.querySelector(".goog-te-combo");
    if (combo) {
      combo.value = code;
      combo.dispatchEvent(new Event("change"));
    } else {
      window.location.reload();
    }
  };

  return (
    <span className="inline-flex items-center gap-1 sm:gap-1.5 notranslate" data-testid="translate-page">
      <Languages className="w-3.5 h-3.5 text-blue-600 hidden sm:block" />
      <select value={lang} onChange={(e) => change(e.target.value)} data-testid="translate-page-select"
        aria-label="Translate this page" translate="no"
        className={`notranslate bg-white text-zinc-700 text-xs border border-zinc-300 rounded px-1.5 py-1 focus:outline-none focus:border-blue-500 cursor-pointer [&>option]:text-zinc-900 min-w-0 sm:w-auto ${lang ? "w-[40px] max-[360px]:w-[32px]" : "w-[70px] max-[360px]:w-[50px]"}`}>
        {/* When a translation is active the first entry is an explicit way back to English
            (mobile pickers show the option list without context, so "Translate…" alone reads
            like a placeholder — not a way home). */}
        {LANGS.map(([code, label]) => (
          <option key={code} value={code}>{code ? label : (lang ? "English (original)" : "Translate…")}</option>
        ))}
      </select>
      {lang && (
        <button onClick={() => change("")} data-testid="translate-reset-btn" aria-label="Back to English"
          title="Back to English"
          className="inline-flex items-center justify-center border border-zinc-300 bg-white text-zinc-600 rounded p-[3px] hover:border-blue-500 hover:text-blue-700 transition-colors">
          <X className="w-3.5 h-3.5" />
        </button>
      )}
      <span id="google_translate_element" />
    </span>
  );
}
